import React, { useState, useEffect } from "react";
import { ChevronDown } from "lucide-react";
import { RichText } from "./RichText";
import { useI18n, pickLang } from "../i18n";

// Injects a FAQPage schema.org JSON-LD block into <head> for rich results.
function useFaqJsonLd(id, items) {
  useEffect(() => {
    const valid = (items || []).filter((f) => f.q && f.a_md);
    if (!valid.length) return;
    const data = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: valid.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: {
          "@type": "Answer",
          text: String(f.a_md).replace(/\*\*|__|\[|\]\([^)]*\)|[#>*-]/g, "").trim(),
        },
      })),
    };
    let el = document.getElementById(id);
    if (!el) {
      el = document.createElement("script");
      el.id = id;
      el.type = "application/ld+json";
      document.head.appendChild(el);
    }
    el.textContent = JSON.stringify(data);
    return () => {
      const node = document.getElementById(id);
      if (node) node.remove();
    };
  }, [id, items]);
}

export function FaqSection({ items = [], title, subtitle, jsonLdId = "faq-jsonld", testid = "faq-section", className = "", contained = true }) {
  const [open, setOpen] = useState(0);
  const { lang } = useI18n();
  const valid = (items || [])
    .map((f) => ({ q: pickLang(f.q, lang), a_md: pickLang(f.a_md, lang) }))
    .filter((f) => f.q);
  useFaqJsonLd(jsonLdId, valid);
  if (!valid.length) return null;

  return (
    <section className={`py-16 ${className}`} aria-labelledby={`${testid}-heading`} data-testid={testid}>
      <div className={contained ? "max-w-3xl mx-auto px-5 sm:px-8" : ""}>
        <h2 id={`${testid}-heading`} className="font-head font-extrabold text-3xl sm:text-4xl mb-3">
          {title}
        </h2>
        {subtitle && <p className="text-nbblack/60 text-lg mb-8">{subtitle}</p>}
        <div className="space-y-3 mt-6">
          {valid.map((f, i) => {
            const isOpen = open === i;
            return (
              <div key={i} className="bg-white nb-border nb-shadow-sm rounded-2xl overflow-hidden" data-testid={`${testid}-item-${i}`}>
                <button
                  onClick={() => setOpen(isOpen ? -1 : i)}
                  data-testid={`${testid}-q-${i}`}
                  className="w-full flex items-center justify-between gap-4 text-left px-5 py-4 font-head font-bold text-base sm:text-lg nb-press"
                  aria-expanded={isOpen}
                >
                  <span>{f.q}</span>
                  <ChevronDown size={20} className={`shrink-0 transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`} />
                </button>
                {isOpen && (
                  <div className="px-5 pb-5 -mt-1 text-nbblack/75" data-testid={`${testid}-a-${i}`}>
                    <RichText md={f.a_md} />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

export default FaqSection;
